import { useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "./ErrorNote";
import { SlotPicker, type Slot } from "./SlotPicker";

type Props = {
  appointmentId: string;
  patientName: string;
  staffId: string;
  was: string;
  onClose: () => void;
  onMoved: () => void;
};

function when(iso: string) {
  return new Date(iso).toLocaleString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Move a booking to another time (SCH-F-025).
 *
 * The server does this as one transaction: it claims the new slot before it
 * releases the old one. A patient left with no appointment because the new slot
 * went while the old one was being released has been failed by the software, so
 * this screen never has to undo half a move.
 *
 * A lost race comes back as a 409 and the picker is still on screen with the
 * rest of the day on it, which is the point of keeping it open.
 */
export function MoveAppointment({
  appointmentId,
  patientName,
  staffId,
  was,
  onClose,
  onMoved,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");

  async function move(slot: Slot) {
    setBusy(true);
    const { error: failed } = await api.PATCH("/portal/appointments/{id}", {
      params: { header: clinicHeader(), path: { id: appointmentId } },
      body: { slot_id: slot.id, reason },
    });
    setBusy(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    onMoved();
  }

  return (
    <div className="panel">
      <div className="row-between">
        <h2>Move {patientName}</h2>
        <button type="button" className="link" onClick={onClose}>
          Close
        </button>
      </div>

      <p className="quiet">Booked for {when(was)}. Pick another time.</p>

      <ErrorNote message={error} />

      <label className="field">
        Why it moved
        <input
          type="text"
          value={reason}
          maxLength={300}
          placeholder="Doctor called away"
          onChange={(event) => setReason(event.target.value)}
        />
      </label>

      <SlotPicker preferStaffId={staffId} onPick={(slot) => void move(slot)} busy={busy} />
    </div>
  );
}
